import type { ApiErrorBody } from '@fametrue/shared';

const CSRF_COOKIE = 'ft_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Errore con il codice stabile restituito dall'API, da tradurre nella UI. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Payload aggiuntivo del codice: elenco delle regole violate per la
   * validazione di un brand, elenco degli avvisi per un import da confermare.
   */
  readonly details: unknown;

  constructor(status: number, code: string, details?: unknown) {
    super(code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const match = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function ensureCsrfToken(): Promise<string | null> {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;

  // Prima del login non esiste ancora alcun token: l'API ne emette uno.
  await fetch('/api/auth/csrf', { credentials: 'same-origin' });
  return readCookie(CSRF_COOKIE);
}

/**
 * Refresh in corso, condiviso.
 *
 * Se più chiamate scadono insieme (tipico all'apertura di una vista che carica
 * dati in parallelo) devono attendere lo stesso refresh: rotazioni concorrenti
 * dello stesso token verrebbero lette dal backend come riuso e chiuderebbero la
 * sessione.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const token = await ensureCsrfToken();
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
        headers: token ? { [CSRF_HEADER]: token } : {},
      });
      return res.ok;
    } finally {
      // Il reset avviene al giro di event loop successivo, così le chiamate
      // in coda leggono comunque il risultato di questo tentativo.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Interno: impedisce il ciclo refresh → 401 → refresh. */
  retryOnUnauthorized?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers();

  if (!SAFE_METHODS.has(method)) {
    const token = await ensureCsrfToken();
    if (token) headers.set(CSRF_HEADER, token);
  }
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  // Access token scaduto: si tenta una rotazione e si ripete una sola volta.
  if (
    response.status === 401 &&
    options.retryOnUnauthorized !== false &&
    !path.startsWith('/api/auth/refresh') &&
    !path.startsWith('/api/auth/login')
  ) {
    if (await refreshSession()) {
      return apiRequest<T>(path, { ...options, retryOnUnauthorized: false });
    }
  }

  if (!response.ok) {
    let code = 'internal_error';
    let details: unknown;
    try {
      const body = (await response.json()) as ApiErrorBody;
      if (body.error?.code) code = body.error.code;
      details = body.error?.details;
    } catch {
      // Risposta non JSON: resta il codice generico.
    }
    throw new ApiError(response.status, code, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
